import { useEffect, useRef, useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import { StyledHoursList, StyledHour } from './style';
import { CONSTANTS } from './constants';
import { AM_PM } from '../constants';

export default function HoursList(props) {
  const dummy = useRef();
  const { onSelectHour, inputHour, is12HourTimeFormat, minHours, maxHours, currentTimeFormat } = props;
  const [selectedHour, setSelectedHour] = useState(null);
  useEffect(() => {
    if (inputHour !== null && inputHour !== undefined) {
      setSelectedHour(String(inputHour).padStart(2, '0'));
    }
  }, [inputHour, selectedHour, is12HourTimeFormat]);

  const convertTo24Hour = (hour12, timeFormat) => {
    const hourNum = parseInt(hour12, 10);
    if (timeFormat === 'am') {
      return hourNum === 12 ? 0 : hourNum; // 12 AM = 0, 1-11 AM = 1-11
    }
    return hourNum === 12 ? 12 : hourNum + 12; // 12 PM = 12, 1-11 PM = 13-23
  };

  const hourSelectHandlers = useMemo(() => {
    const hours = is12HourTimeFormat ? CONSTANTS.MIDDAY_HOURS_LIST : CONSTANTS.HOURS_LIST;
    return hours.reduce((handlers, hour) => {
      handlers[hour] = () => {
        setSelectedHour(hour);
        onSelectHour(hour);
      };
      return handlers;
    }, {});
  }, [onSelectHour, is12HourTimeFormat]);

  const selectedHours = useMemo(() => {
    const hours = is12HourTimeFormat ? CONSTANTS.MIDDAY_HOURS_LIST : CONSTANTS.HOURS_LIST;
    const normalizedSelectedHour = selectedHour?.length === 1 ? `0${selectedHour}` : selectedHour;
    return hours.reduce((selected, hour) => {
      selected[hour] = hour === normalizedSelectedHour;
      return selected;
    }, {});
  }, [selectedHour, is12HourTimeFormat]);

  const disabledHours = useMemo(() => {
    const hours = is12HourTimeFormat ? CONSTANTS.MIDDAY_HOURS_LIST : CONSTANTS.HOURS_LIST;
    return hours.reduce((disabled, hour) => {
      const hourNum = parseInt(hour, 10);
      
      if (is12HourTimeFormat && currentTimeFormat) {
        const hour24 = convertTo24Hour(hour, currentTimeFormat);
        disabled[hour] = hour24 < minHours || hour24 > maxHours;
      } else {
        disabled[hour] = hourNum < minHours || hourNum > maxHours;
      }
      return disabled;
    }, {});
  }, [is12HourTimeFormat, currentTimeFormat, minHours, maxHours]);

  useEffect(() => {
    if (dummy?.current && !is12HourTimeFormat) {
      dummy.current.scrollIntoView();
    }
  }, [is12HourTimeFormat]);

  return (
    <StyledHoursList data-testid="hours-list">
      {is12HourTimeFormat ?
        CONSTANTS.MIDDAY_HOURS_LIST.map((middayHour) => (
          <StyledHour
            key={middayHour}
            isSelected={selectedHours[middayHour]}
            onClick={hourSelectHandlers[middayHour]}
            aria-selected={selectedHours[middayHour] ? true : undefined}
            disabled={disabledHours[middayHour]}
          >
            {middayHour}
          </StyledHour>
        )) :
        CONSTANTS.HOURS_LIST.map((hour) => (
          <StyledHour
            key={hour}
            isSelected={selectedHours[hour]}
            onClick={hourSelectHandlers[hour]}
            aria-selected={selectedHours[hour] ? true : undefined}
            disabled={disabledHours[hour]}
          >
            {hour}
          </StyledHour>
        ))
      }
      <div ref={dummy} />
    </StyledHoursList>
  );
}

HoursList.propTypes = {
  onSelectHour: PropTypes.func.isRequired,
  inputHour: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  is12HourTimeFormat: PropTypes.bool,
  minHours: PropTypes.number,
  maxHours: PropTypes.number,
  currentTimeFormat: PropTypes.oneOf(AM_PM),
};
